import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useForm, Controller, type UseFormRegisterReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Save } from 'lucide-react';
import { useCreateDiveLog, type DiveLogInput } from '@/data/dive-logs';
import type { DiveSite } from '@/types/api';
import { DivesitePicker } from '@/components/divesite-picker/DivesitePicker';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { cn } from '@/lib/utils';

// Empty string / null / undefined → null so optional numbers don't trip min/max checks.
const optionalNumber = (opts: { min?: number; max?: number; int?: boolean } = {}) =>
  z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? null : v),
    z
      .union([
        z.null(),
        opts.int
          ? z.coerce.number().int().refine((n) => (opts.min === undefined || n >= opts.min) && (opts.max === undefined || n <= opts.max), {
              message: `Wert muss zwischen ${opts.min ?? '-∞'} und ${opts.max ?? '∞'} liegen.`,
            })
          : z.coerce.number().refine((n) => (opts.min === undefined || n >= opts.min) && (opts.max === undefined || n <= opts.max), {
              message: `Wert muss zwischen ${opts.min ?? '-∞'} und ${opts.max ?? '∞'} liegen.`,
            }),
      ])
      .nullable(),
  );

const optionalString = () =>
  z.preprocess((v) => (v === '' || v === undefined ? null : v), z.string().nullable());

const schema = z.object({
  title:              z.string().min(1, 'Titel ist Pflicht.'),
  diveDate:           z.string().min(1, 'Datum eingeben.'),
  diveTimeMinutes:    z.coerce.number().int().min(1, 'Mindestens 1 min.').max(300, 'Höchstens 300 min.'),
  maxDepth:           z.coerce.number().int().min(1, 'Mindestens 1 m.').max(120, 'Höchstens 120 m.'),
  waterTemperature:   optionalNumber({ min: -5, max: 40 }),
  type:               optionalString(),
  visibility:         optionalString(),
  current:            optionalString(),
  wetSuitType:        optionalString(),
  usedWeight:         optionalNumber({ int: true, min: 0,  max: 50  }),
  airType:            optionalString(),
  nitroxO2Percentage: optionalNumber({ int: true, min: 21, max: 100 }),
  initialPressure:    optionalNumber({ int: true, min: 0,  max: 400 }),
  finalPressure:      optionalNumber({ int: true, min: 0,  max: 400 }),
  rating:             optionalNumber({ int: true, min: 1,  max: 5   }),
  buddies:            optionalString(),
  description:        optionalString(),
});
type FormValues = z.infer<typeof schema>;

const STEPS = [
  { key: 'when', label: 'Wann & Wo' },
  { key: 'dive', label: 'Tauchgang' },
  { key: 'gear', label: 'Ausrüstung' },
  { key: 'meta', label: 'Notizen' },
] as const;

export function DiveLogCreatePage() {
  useDocumentTitle('Neuer Tauchgang');
  const navigate = useNavigate();
  const create = useCreateDiveLog();
  const [step, setStep] = useState(0);
  const [diveSite, setDiveSite] = useState<DiveSite | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    trigger,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      diveDate: new Date().toISOString().slice(0, 10),
      airType:  'normal',
    },
  });

  const airType = watch('airType');

  // Map every field name to the step it lives on, so we can jump there on validation errors.
  const FIELD_STEP: Record<keyof FormValues, number> = {
    title: 0, diveDate: 0,
    diveTimeMinutes: 1, maxDepth: 1, waterTemperature: 1, type: 1, visibility: 1, current: 1,
    wetSuitType: 2, usedWeight: 2, airType: 2, nitroxO2Percentage: 2, initialPressure: 2, finalPressure: 2,
    rating: 3, buddies: 3, description: 3,
  };

  const onInvalid = (errs: typeof errors) => {
    const firstField = Object.keys(errs)[0] as keyof FormValues | undefined;
    if (firstField) {
      const targetStep = FIELD_STEP[firstField];
      if (targetStep !== undefined && targetStep !== step) {
        setStep(targetStep);
      }
      const msg = errs[firstField]?.message;
      toast.error(msg ? `${String(firstField)}: ${msg}` : 'Bitte fehlende Felder ergänzen.');
    } else {
      toast.error('Bitte fehlende Felder ergänzen.');
    }
  };

  const onSubmit = async (values: FormValues) => {
    if (!diveSite) {
      toast.error('Bitte Tauchplatz wählen.');
      setStep(0);
      return;
    }
    try {
      const payload: DiveLogInput = {
        ...values,
        diveSiteId: diveSite.id,
        waterTemperature:   values.waterTemperature ?? null,
        usedWeight:         values.usedWeight ?? null,
        nitroxO2Percentage: values.nitroxO2Percentage ?? null,
        initialPressure:    values.initialPressure ?? null,
        finalPressure:      values.finalPressure ?? null,
        rating:             values.rating ?? null,
      };
      const log = await create.mutateAsync(payload);
      toast.success('Tauchgang gespeichert.');
      void navigate({ to: '/logs/$id', params: { id: String(log.id) } });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    }
  };

  const next = async () => {
    let ok = true;
    if (step === 0) {
      ok = (await trigger(['title', 'diveDate'])) && diveSite !== null;
      if (diveSite === null) toast.error('Bitte Tauchplatz wählen.');
    } else if (step === 1) {
      ok = await trigger(['diveTimeMinutes', 'maxDepth']);
    }
    if (ok && step < STEPS.length - 1) setStep((s) => s + 1);
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6 md:p-8">
      <header>
        <h1 className="text-2xl font-semibold">Neuer Tauchgang</h1>
        <p className="text-sm text-[var(--muted-foreground)]">In 4 kurzen Schritten loggen.</p>
      </header>

      <div className="flex items-center gap-2">
        {STEPS.map((s, idx) => (
          <button
            key={s.key}
            type="button"
            onClick={() => idx < step && setStep(idx)}
            className={cn(
              'flex-1 rounded-full px-3 py-1.5 text-xs font-medium transition',
              idx === step
                ? 'bg-ocean-700 text-white'
                : idx < step
                  ? 'bg-ocean-100 text-ocean-700 hover:bg-ocean-200 dark:bg-ocean-800 dark:text-ocean-200'
                  : 'bg-[var(--muted)] text-[var(--muted-foreground)]',
            )}
            aria-current={idx === step ? 'step' : undefined}
          >
            {idx + 1}. {s.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
        {step === 0 && (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="space-y-1.5">
                <Label htmlFor="title">Titel</Label>
                <Input id="title" placeholder="z.B. Morning dive at Elphinstone" {...register('title')} />
                {errors.title && <p className="text-xs text-[var(--destructive)]">{errors.title.message}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="diveDate">Datum</Label>
                <Input id="diveDate" type="date" {...register('diveDate')} />
              </div>

              <div className="space-y-1.5">
                <Label>Tauchplatz</Label>
                <DivesitePicker value={diveSite} onChange={setDiveSite} />
                {diveSite === null && (
                  <p className="text-xs text-[var(--muted-foreground)]">
                    Falls dein Tauchplatz noch nicht in der DB ist: einfach „+ Neuen Tauchplatz anlegen" wählen, Karte klicken, fertig.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {step === 1 && (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="diveTimeMinutes">Dauer (min)</Label>
                  <Input id="diveTimeMinutes" type="number" inputMode="numeric" {...register('diveTimeMinutes')} />
                  {errors.diveTimeMinutes && <p className="text-xs text-[var(--destructive)]">{errors.diveTimeMinutes.message}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="maxDepth">Max. Tiefe (m)</Label>
                  <Input id="maxDepth" type="number" inputMode="numeric" {...register('maxDepth')} />
                  {errors.maxDepth && <p className="text-xs text-[var(--destructive)]">{errors.maxDepth.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="waterTemperature">Wassertemp. (°C)</Label>
                  <Input id="waterTemperature" type="number" step="0.5" {...register('waterTemperature')} />
                </div>
                <SelectField label="Typ" id="type" register={register('type')} options={[
                  { value: '', label: '–' },
                  { value: 'shore', label: 'Shore' },
                  { value: 'boat',  label: 'Boot' },
                  { value: 'misc',  label: 'Sonstige' },
                ]} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <SelectField label="Sicht" id="visibility" register={register('visibility')} options={[
                  { value: '',       label: '–' },
                  { value: 'good',   label: 'Gut' },
                  { value: 'medium', label: 'Mittel' },
                  { value: 'bad',    label: 'Schlecht' },
                ]} />
                <SelectField label="Strömung" id="current" register={register('current')} options={[
                  { value: '',       label: '–' },
                  { value: 'none',   label: 'Keine' },
                  { value: 'little', label: 'Wenig' },
                  { value: 'medium', label: 'Mittel' },
                  { value: 'strong', label: 'Stark' },
                ]} />
              </div>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <SelectField label="Anzugtyp" id="wetSuitType" register={register('wetSuitType')} options={[
                { value: '',         label: '–' },
                { value: 'none',     label: 'Keiner' },
                { value: 'shorty',   label: 'Shorty' },
                { value: 'full-3mm', label: 'Full 3mm' },
                { value: 'full-5mm', label: 'Full 5mm' },
                { value: 'full-7mm', label: 'Full 7mm' },
                { value: 'drysuit',  label: 'Trockenanzug' },
              ]} />

              <div className="space-y-1.5">
                <Label htmlFor="usedWeight">Blei (kg)</Label>
                <Input id="usedWeight" type="number" inputMode="numeric" {...register('usedWeight')} />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <SelectField label="Atemgas" id="airType" register={register('airType')} options={[
                  { value: 'normal', label: 'Normal' },
                  { value: 'nitrox', label: 'Nitrox' },
                ]} />
                {airType === 'nitrox' && (
                  <div className="space-y-1.5">
                    <Label htmlFor="nitroxO2Percentage">O₂-Anteil (%)</Label>
                    <Input id="nitroxO2Percentage" type="number" inputMode="numeric" placeholder="32" {...register('nitroxO2Percentage')} />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="initialPressure">Druck Start (bar)</Label>
                  <Input id="initialPressure" type="number" inputMode="numeric" {...register('initialPressure')} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="finalPressure">Druck Ende (bar)</Label>
                  <Input id="finalPressure" type="number" inputMode="numeric" {...register('finalPressure')} />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div className="space-y-1.5">
                <Label htmlFor="buddies">Buddies</Label>
                <Input id="buddies" placeholder="Max, Lisa, …" {...register('buddies')} />
              </div>

              <Controller
                control={control}
                name="rating"
                render={({ field }) => (
                  <div className="space-y-1.5">
                    <Label>Bewertung</Label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((n) => {
                        const active = (field.value ?? 0) >= n;
                        return (
                          <button
                            key={n}
                            type="button"
                            onClick={() => field.onChange(field.value === n ? null : n)}
                            className={cn(
                              'h-10 w-10 rounded-md text-lg transition',
                              active
                                ? 'bg-coral-500 text-white hover:bg-coral-600'
                                : 'bg-[var(--muted)] text-[var(--muted-foreground)] hover:bg-[var(--muted)]/80',
                            )}
                            aria-label={`${n} ${n === 1 ? 'Stern' : 'Sterne'}`}
                            aria-pressed={active}
                          >
                            ★
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              />

              <div className="space-y-1.5">
                <Label htmlFor="description">Notizen</Label>
                <Textarea id="description" rows={4} placeholder="Tier-Sichtungen, Strömung, besondere Vorkommnisse…" {...register('description')} />
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-between">
          <Button type="button" variant="outline" onClick={prev} disabled={step === 0}>
            <ChevronLeft className="h-4 w-4" /> Zurück
          </Button>
          {step < STEPS.length - 1 ? (
            <Button type="button" onClick={next}>
              Weiter <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? <Spinner /> : <><Save className="h-4 w-4" /> Speichern</>}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}

function SelectField({
  label,
  id,
  register,
  options,
}: {
  label: string;
  id: string;
  register: UseFormRegisterReturn;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <select
        id={id}
        className="h-10 w-full rounded-md border border-[var(--border)] bg-[var(--input)] px-3 text-sm"
        {...register}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}
